import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { useConfigurator, WIZARD_STEPS } from '../../../store/configurator';
import { MobileHeader } from '../../components/MobileHeader';
import { MobileInlinePreview } from '../../components/MobileInlinePreview';
import { MobileSitePreview } from '../../components/MobileSitePreview';
import { PlayIcon, ColorCard, StyleCard } from '../designCards';
import {
  Screen,
  PhoneClip,
  PhoneHolder,
  Scrim,
  PreviewPill,
  PreviewPillLabel,
  Sheet,
  Tabs,
  Tab,
  Carousel,
  Cta,
} from '../MobileDesignFlow/MobileDesignFlow.styles';

type SubStep = 'colors' | 'styles';

const ORDER: SubStep[] = ['colors', 'styles'];

/**
 * v0 mobile step 2 — Colors & Art Style. Two sub-steps (Colors, Art Style) over the live
 * phone preview; no intro animation (the preview is already warm from the Theme step). Back
 * from Colors returns to the Theme step; advancing past Art Style goes to Payment review.
 * Split from v3's combined MobileDesignFlow.
 */
export const MobileColorsFlow = (): React.ReactElement => {
  const { t } = useTranslation();
  const { colors, styles, selection, selectedColor, previewUrl, setColor, setStyle, saveNow } = useConfigurator();
  const navigate = useNavigate();

  // Translated tab + CTA labels (the SubStep keys stay stable English logic ids).
  const tabLabel: Record<SubStep, string> = {
    colors: t('Colors'),
    styles: t('Art Style'),
  };
  const ctaLabel: Record<SubStep, string> = {
    colors: t('Continue to Art Style'),
    styles: t('Continue to review'),
  };

  const [subStep, setSubStep] = useState<SubStep>('colors');
  const [previewOpen, setPreviewOpen] = useState(false);

  const stepIdx = ORDER.indexOf(subStep);
  const goBack = (): void => {
    if (stepIdx > 0) setSubStep(ORDER[stepIdx - 1]);
    else navigate(WIZARD_STEPS[0].path);
  };
  const goNext = (): void => {
    if (subStep === 'styles') {
      // Leaving Colors & Art Style → persist the full configuration on advance.
      saveNow();
      navigate(WIZARD_STEPS[2].path);
    } else setSubStep(ORDER[stepIdx + 1]);
  };

  return (
    <Screen>
      <MobileHeader activeStep={1} onBack={goBack} />

      <PhoneClip>
        <PhoneHolder $in>
          <MobileInlinePreview />
        </PhoneHolder>
      </PhoneClip>

      <Scrim $shown />

      <PreviewPill
        type="button"
        onClick={() => setPreviewOpen(true)}
        data-ga-id="cfg-mobile-colors-preview-open"
        $shown
        aria-label={t('Preview')}
      >
        <PlayIcon />
        <PreviewPillLabel text="Preview" tag="span" />
      </PreviewPill>

      <Sheet $up>
        <Tabs>
          {ORDER.map((s) => (
            <Tab key={s} type="button" onClick={() => setSubStep(s)} data-ga-id={`cfg-mobile-colors-tab-${s}`} $active={s === subStep}>
              <Text text={tabLabel[s]} tag="span" />
            </Tab>
          ))}
        </Tabs>

        <Carousel>
          {subStep === 'colors' &&
            colors.map((c) => (
              <ColorCard key={c.id} color={c} selected={c.id === selection.colorId} onClick={() => setColor(c.id)} />
            ))}
          {subStep === 'styles' &&
            styles.map((s) => (
              <StyleCard key={s.id} style={s} selected={s.id === selection.styleId} onClick={() => setStyle(s.id)} />
            ))}
        </Carousel>
      </Sheet>

      <Cta type="button" onClick={goNext} data-ga-id="cfg-mobile-colors-cta-next" $shown>
        <Text text={ctaLabel[subStep]} tag="span" />
      </Cta>

      <MobileSitePreview open={previewOpen} url={previewUrl} bgColor={selectedColor?.bg} onClose={() => setPreviewOpen(false)} />
    </Screen>
  );
};

export default MobileColorsFlow;
